import React, { useState, useRef } from 'react';
import { ChevronDown, ImagePlus, Loader2, MoreVertical, Trash2, X } from 'lucide-react';
import {
  type MusicTrack,
  type MusicCategory,
  MUSIC_CATEGORIES,
  updateMusicTrack,
  uploadFile,
  deleteMusicTrackFull,
  toggleTrackExcluded,
  incrementPlayCount,
} from '@/services/musicTracksService';
import { TrackCover } from './TrackCover';
import { TrackPlayer } from './TrackPlayer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { cropImageToSquare } from '@/utils/cropImageToSquare';

interface TrackEditPanelProps {
  track: MusicTrack | null;
  onClose: () => void;
  /** Called with updated track so parent can update state without refetch */
  onSaved: (updated: MusicTrack) => void;
  /** Called with deleted track id so parent can remove from list without refetch */
  onDeleted?: (trackId: string) => void;
  activePlayerStoragePath?: string | null;
  onPlayStart?: (storagePath: string) => void;
}

export function TrackEditPanel({
  track,
  onClose,
  onSaved,
  onDeleted,
  activePlayerStoragePath = null,
  onPlayStart,
}: TrackEditPanelProps) {
  const [title, setTitle] = useState(track?.title ?? '');
  const [artist, setArtist] = useState(track?.artist ?? '');
  const [album, setAlbum] = useState(track?.album ?? '');
  const [albumArtist, setAlbumArtist] = useState(track?.albumArtist ?? '');
  const [category, setCategory] = useState<MusicCategory>(track?.category ?? 'ambient');
  const [year, setYear] = useState(track?.year?.toString() ?? '');
  const [genre, setGenre] = useState(track?.genre ?? '');
  const [comment, setComment] = useState(track?.comment ?? '');
  const [excluded, setExcluded] = useState(track?.excluded ?? false);
  const [saving, setSaving] = useState(false);
  const [togglingExcluded, setTogglingExcluded] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleReplaceCover = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!track || !file || !file.type.startsWith('image/')) return;
    setReplacing(true);
    try {
      // Обрезаем изображение до квадрата по центру
      const outputFormat = file.name.toLowerCase().endsWith('.png') ? 'image/png' : 'image/jpeg';
      const croppedBlob = await cropImageToSquare(file, outputFormat);
      
      const ext = file.name.toLowerCase().endsWith('.png') ? 'png' : 'jpg';
      const coverPath = `covers/${track.id}.${ext}`;
      await uploadFile(coverPath, croppedBlob);
      await updateMusicTrack(track.id, { coverPath });
      onSaved({
        ...track,
        coverPath,
        updatedAt: new Date().toISOString(),
      });
    } finally {
      setReplacing(false);
      e.target.value = '';
    }
  };

  React.useEffect(() => {
    if (track) {
      setTitle(track.title ?? '');
      setArtist(track.artist ?? '');
      setAlbum(track.album ?? '');
      setAlbumArtist(track.albumArtist ?? '');
      setCategory(track.category ?? 'ambient');
      setYear(track.year?.toString() ?? '');
      setGenre(track.genre ?? '');
      setComment(track.comment ?? '');
      setExcluded(track.excluded ?? false);
    }
  }, [track]);

  const handleSave = async () => {
    if (!track) return;
    setSaving(true);
    try {
      const yearNum = year ? (parseInt(year, 10) || undefined) : undefined;
      await updateMusicTrack(track.id, {
        title,
        artist,
        album,
        albumArtist: albumArtist || undefined,
        category,
        year: yearNum,
        genre: genre || undefined,
        comment: comment || undefined,
        excluded: excluded || undefined,
      });
      onSaved({
        ...track,
        title,
        artist,
        album,
        albumArtist: albumArtist || undefined,
        category,
        year: yearNum,
        genre: genre || undefined,
        comment: comment || undefined,
        excluded,
      });
    } finally {
      setSaving(false);
    }
  };

  if (!track) return null;

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="flex shrink-0 items-center justify-between border-b px-4 py-3 gap-2">
        <h3 className="font-semibold truncate flex-1 min-w-0">Edit Track</h3>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="shrink-0 h-8 w-8">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {onDeleted && (
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onSelect={() => setDeleteDialogOpen(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete track
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="ghost" size="icon" className="shrink-0" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="p-4 space-y-4">
          <div className="flex flex-col items-center gap-4">
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleReplaceCover}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={replacing}
              className="relative flex h-56 w-56 shrink-0 sm:h-64 sm:w-64 items-center justify-center overflow-hidden rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              <TrackCover
                coverPath={track.coverPath}
                size="lg"
                className="h-56 w-56 sm:h-64 sm:w-64"
                cacheBuster={track.updatedAt}
              />
              {/* Спиннер загрузки - показывается постоянно во время загрузки */}
              {replacing && (
                <span className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/60">
                  <Loader2 className="h-10 w-10 animate-spin text-white" />
                </span>
              )}
              {/* Иконка замены - показывается только при наведении, если не идет загрузка */}
              {!replacing && (
                <span className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/40 opacity-0 transition-opacity hover:opacity-100">
                  <ImagePlus className="h-10 w-10 text-white" />
                </span>
              )}
            </button>
            <div className="w-full">
              <TrackPlayer
                storagePath={track.storagePath}
                duration={track.duration}
                activeStoragePath={activePlayerStoragePath}
                onPlayStart={onPlayStart ? () => onPlayStart(track.storagePath) : undefined}
                trackId={track.id}
                onTrackEnded={(id) => incrementPlayCount(id).catch(() => {})}
              />
            </div>
          </div>

          <div className="border-t pt-4 space-y-4">
        <div className="space-y-2">
          <Label htmlFor="edit-title">Title</Label>
          <Input
            id="edit-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label>Category</Label>
          <Select value={category} onValueChange={(v) => setCategory(v as MusicCategory)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MUSIC_CATEGORIES.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between space-x-2 py-2">
          <div className="space-y-0.5">
            <Label htmlFor="edit-excluded" className="text-sm font-medium">
              Include in playlist
            </Label>
            <p className="text-xs text-muted-foreground">
              {excluded ? 'Track is excluded from playlist' : 'Track is included in playlist'}
            </p>
          </div>
          <Switch
            id="edit-excluded"
            checked={!excluded}
            disabled={togglingExcluded}
            onCheckedChange={async (checked) => {
              if (!track) return;
              const newExcluded = !checked;
              setTogglingExcluded(true);
              try {
                await toggleTrackExcluded(track.id, newExcluded);
                setExcluded(newExcluded);
                onSaved({
                  ...track,
                  excluded: newExcluded,
                  updatedAt: new Date().toISOString(),
                });
              } catch (err) {
                console.error('Failed to toggle excluded status:', err);
              } finally {
                setTogglingExcluded(false);
              }
            }}
          />
        </div>

        <Button
          className="w-full"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save'
          )}
        </Button>

        <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between px-3 py-2 h-auto font-normal text-muted-foreground">
              <span>Details</span>
              <ChevronDown className={cn('h-4 w-4 transition-transform', detailsOpen && 'rotate-180')} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="edit-artist">Artist</Label>
              <Input
                id="edit-artist"
                value={artist}
                onChange={(e) => setArtist(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-year">Year</Label>
                <Input
                  id="edit-year"
                  type="number"
                  min={1900}
                  max={2100}
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  placeholder="2024"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-genre">Genre</Label>
                <Input
                  id="edit-genre"
                  value={genre}
                  onChange={(e) => setGenre(e.target.value)}
                  placeholder="Genre"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-comment">Comment</Label>
              <Input
                id="edit-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Comment"
              />
            </div>
            <div className="space-y-1 text-xs text-muted-foreground border rounded-lg p-3 bg-muted/30">
              <p className="font-medium text-foreground/80">Technical info</p>
              {[
                track.bitrate && `Bitrate: ${Math.round(track.bitrate / 1000)} kbps`,
                track.sampleRate && `Sample rate: ${track.sampleRate} Hz`,
                track.numberOfChannels && `Channels: ${track.numberOfChannels}`,
                track.codec && `Codec: ${track.codec}`,
                track.bpm && `BPM: ${track.bpm}`,
                track.fileSize && `Size: ${(track.fileSize / 1024).toFixed(1)} KB`,
                track.originalFilename && `File: ${track.originalFilename}`,
                track.trackNo && `Track: ${track.trackNo}${track.diskNo ? ` / Disc ${track.diskNo}` : ''}`,
                track.isrc && `ISRC: ${track.isrc}`,
              ]
                .filter(Boolean)
                .map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              {track.playCount > 0 && <p>Plays: {track.playCount}</p>}
            </div>
          </CollapsibleContent>
        </Collapsible>

        {onDeleted && (
          <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete track?</AlertDialogTitle>
                <AlertDialogDescription>
                  Track «{track.title}» will be removed from the database and Storage. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={async (e) => {
                    e.preventDefault();
                    if (!track) return;
                    const trackId = track.id;
                    setDeleting(true);
                    try {
                      await deleteMusicTrackFull(track);
                      setDeleteDialogOpen(false);
                      onClose();
                      onDeleted?.(trackId);
                    } finally {
                      setDeleting(false);
                    }
                  }}
                  disabled={deleting}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
          </div>
        </div>
      </div>
    </div>
  );
}
